import type { JSX, VoidComponent } from "solid-js";
import { createMemo } from "solid-js";
import type { ExtendableInputProps } from "./Input";
import Input from "./Input";
import { PasswordInput as ArkPasswordInput } from "@ark-ui/solid";
import { Icon } from "../icon/Icon";
import { useTranslation } from "../../state/i18n";

type PasswordProps = {
  id: string;
  ariaLabel?: string;
  value?: string;
  disabled?: boolean;
  placeholder?: string;
  errorMessage?: string;
  onBlur?: JSX.FocusEventHandler<HTMLInputElement, FocusEvent>;
  onInput?: JSX.InputEventHandler<HTMLInputElement, InputEvent>;
  autoComplete?: boolean;
};
type PasswordInputProps = PasswordProps & ExtendableInputProps;

const BasePasswordInput: VoidComponent<PasswordProps> = (props) => {
  const { t } = useTranslation();
  const hasError = createMemo(() => props.errorMessage !== undefined);
  const isDisabled = createMemo(() => props.disabled === true);
  const classes = createMemo(() => {
    if (hasError()) {
      return "bg-surface-destructive border-error";
    } else if (isDisabled()) {
      return "bg-surface-disabled border-disabled";
    } else {
      return "bg-surface-default border-default";
    }
  });

  return (
    <ArkPasswordInput.Root
      aria-label={props.ariaLabel}
      ignorePasswordManagers={isDisabled()}
      class="w-full"
      translations={{
        visibilityTrigger: (visible) =>
          visible
            ? t(($) => $.PASSWORD_INPUT_VISIBILITY_TRIGGER_HIDE)
            : t(($) => $.PASSWORD_INPUT_VISIBILITY_TRIGGER_SHOW),
      }}
    >
      <ArkPasswordInput.Context>
        {(context) => (
          <ArkPasswordInput.Control
            class="relative flex items-center truncate rounded-sm border text-base-regular text-primary placeholder:text-base-regular [&:has(input:focus)]:outline-default"
            classList={{
              [classes()]: true,
            }}
          >
            <ArkPasswordInput.Input
              id={props.id}
              value={props.value}
              placeholder={props.placeholder}
              disabled={isDisabled()}
              class="grow py-2 pr-10 pl-2 outline-none"
              classList={{ [classes()]: true }}
              onInput={(e) => {
                props.onInput?.(e);
              }}
              onBlur={(e) => {
                props.onBlur?.(e);
              }}
            />
            <ArkPasswordInput.VisibilityTrigger
              tabIndex={0}
              class="absolute right-1 flex size-7.5 items-center justify-center rounded-sm bg-button-bg-tertiary-default p-1.5 hover:bg-button-bg-tertiary-hover focus-visible:outline-default active:bg-button-bg-tertiary-pressed"
              title={
                context().visible
                  ? t(($) => $.PASSWORD_INPUT_VISIBILITY_TRIGGER_HIDE)
                  : t(($) => $.PASSWORD_INPUT_VISIBILITY_TRIGGER_SHOW)
              }
              onClick={() => context().toggleVisible()}
            >
              <ArkPasswordInput.Indicator
                fallback={<Icon class="icon-button-icon-tertiary-soft" name="eyeOff" type="solid" size="sm" />}
              >
                <Icon name="eye" type="solid" size="sm" class="icon-button-icon-tertiary-harsh" />
              </ArkPasswordInput.Indicator>
            </ArkPasswordInput.VisibilityTrigger>
          </ArkPasswordInput.Control>
        )}
      </ArkPasswordInput.Context>
    </ArkPasswordInput.Root>
  );
};

export const PasswordInput: VoidComponent<Omit<PasswordInputProps, "id">> = (props) => (
  <Input {...props}>{(id) => <BasePasswordInput {...props} id={id} />}</Input>
);

export const CompactPasswordInput: VoidComponent<PasswordProps> = (props) => <BasePasswordInput {...props} />;
